import React, { useState, useContext } from "react";
import moment from "moment";
import { AccountLayout } from "./accountUi";
import { apiGet } from "../../Utils/apiFetch";
import apiPath from "../../Utils/apiPath";
import AuthContext from "../../context/AuthContext";
import { PageShell, FilterCard, ActionButton, TableCard, selectStyle, DateField, thStyle, tdStyle } from "./accountUi";

/* ───────────────────────────────────────────────────────────────────────────
 * Rolling Commission — reference kingexch111.com/rolling-commission.
 * Data Source + Sport dropdowns, date range, "Get Commission", then a table
 * of Type / Total Stack / Total Commission.
 *
 * There is no dedicated user commission endpoint, so this aggregates settled
 * bet-history by market type (SPORTSBOOK / MATCH_ODDS / FANCY) and sums stake
 * and commission.
 * ─────────────────────────────────────────────────────────────────────────── */

const RollingCommission = () => {
  const { user, profileData } = useContext(AuthContext);
  const [sport, setSport] = useState("Cricket");
  const [dataSource, setDataSource] = useState("");
  const [from, setFrom] = useState(moment().subtract(1, "day").format("YYYY-MM-DD"));
  const [to, setTo] = useState(moment().format("YYYY-MM-DD"));
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const sportToEventType = { Cricket: 4, Tennis: 2, Soccer: 1 };

  // User's rolling-commission % (from profile). Commission is charged on net
  // market profit, matching the settlement logic used in the backend.
  const commissionRate =
    parseFloat(profileData?.commission ?? user?.user?.commission ?? 0) || 0;

  const netProfitOf = (b) => {
    const profit = parseFloat(b?.profitAmount || 0) || 0;
    const lose = parseFloat(b?.loseAmount || 0) || 0;
    if (b?.type === "Yes" || b?.type === "No") {
      const yesWon = (parseFloat(b?.decisionRun || 0) || 0) >= (parseFloat(b?.betRun || 0) || 0);
      const isWin = b?.type === "Yes" ? yesWon : !yesWon;
      return isWin ? profit : -lose;
    }
    const won =
      b?.teamSelectionWin && b?.selectionId && String(b.teamSelectionWin) === String(b.selectionId);
    if (b?.betType === "back") return won ? profit : -lose;
    if (b?.betType === "lay") return won ? -lose : profit;
    return profit - lose;
  };

  const aggregate = (bets = []) => {
    const buckets = {}; // type -> { stake, commission }
    bets.forEach((b) => {
      let type = "MATCH_ODDS";
      if (b?.betFaireType === "sportBook") type = "SPORTSBOOK";
      else if (b?.betFaireType === "fancy" || b?.fancyName || b?.type === "Yes" || b?.type === "No")
        type = "FANCY";

      const stake = parseFloat(b?.amount || 0) || 0;
      // Prefer a stored commission; otherwise compute rolling commission
      // (rate % of net profit) so the column is populated from the frontend.
      let comm = parseFloat(b?.commission || 0);
      if (isNaN(comm) || comm === 0) {
        comm = (commissionRate / 100) * Math.max(0, netProfitOf(b));
      }
      if (!buckets[type]) buckets[type] = { stake: 0, commission: 0 };
      buckets[type].stake += stake;
      buckets[type].commission += isNaN(comm) ? 0 : comm;
    });
    return Object.entries(buckets).map(([type, v]) => ({
      type,
      stake: Math.round(v.stake * 100) / 100,
      commission: Math.round(v.commission * 100) / 100,
    }));
  };

  const fetchData = async () => {
    const collected = [];
    for (const betType of ["betfair", "sportBook", "fancy"]) {
      try {
        const { status, data } = await apiGet(apiPath.getHistory, {
          betType,
          status: "completed",
          fromPeriod: from,
          toPeriod: to,
          eventType: sportToEventType[sport],
          page: 1,
          limit: 500,
        });
        if (status === 200 && data?.success) {
          const list = Array.isArray(data?.results) ? data.results : data?.results?.data || [];
          list.forEach((b) => collected.push({ ...b, betFaireType: b?.betFaireType || betType }));
        }
      } catch (e) {
        /* ignore per-type failure */
      }
    }
    setRows(aggregate(collected));
    setLoaded(true);
  };

  return (
    <AccountLayout>
      <PageShell>
        <div style={{ paddingTop: "15px" }}>
          <FilterCard flush>
          <div style={{ display: "flex", gap: "10px" }}>
            <select value={dataSource} onChange={(e) => setDataSource(e.target.value)} style={{ ...selectStyle, flex: 1 }}>
              <option value="" disabled>Data Source</option>
              <option value="live">LIVE DATA</option>
              <option value="backup">BACKUP DATA</option>
              <option value="old">OLD DATA</option>
            </select>
            <select value={sport} onChange={(e) => setSport(e.target.value)} style={{ ...selectStyle, flex: 1 }}>
              <option value="" disabled>Select Sport</option>
              <option>Cricket</option>
              <option>Casino</option>
              <option>Tennis</option>
              <option>Soccer</option>
              <option>Horse Racing</option>
              <option>Politics</option>
              <option>Greyhound Racing</option>
              <option>Basketball</option>
              <option>Lottery</option>
            </select>
          </div>
          <DateField value={from} onChange={setFrom} />
          <DateField value={to} onChange={setTo} />
          <ActionButton onClick={fetchData}>Get Commission</ActionButton>
          </FilterCard>
        </div>

        <TableCard title="Rolling Commission" flush>
          <div style={{ padding: "12px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Total Stack</th>
                <th style={thStyle}>Total Commission</th>
              </tr>
            </thead>
            <tbody>
              {rows.length > 0 ? (
                rows.map((r) => (
                  <tr key={r.type}>
                    <td style={tdStyle}>{r.type}</td>
                    <td style={tdStyle}>{r.stake}</td>
                    <td style={tdStyle}>{r.commission ?? 0}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td style={tdStyle} colSpan={3}>
                    {loaded ? "No records found" : "No data available in table"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </TableCard>
      </PageShell>
    </AccountLayout>
  );
};

export default RollingCommission;
